// Helpers to map obfuscated auth responses and store raw obfuscated fields

const STORAGE_OBF_AUTH_KEY = 'outpoll_auth_obf';

export type ObfuscatedAuth = Partial<{
  c: boolean; // confirmed
  e: string; // email
  n: string; // nickname
  l: string; // language
  a: string; // activityId
  o: boolean; // twoFactorEnabled
}> &
  Record<string, unknown>;

export type MappedAuthData = {
  token?: string;
  user?: {
    id?: string;
    email?: string;
    name?: string;
    confirmed?: boolean;
    userGroup?: string;
    language?: string;
    activityId?: string;
    twoFactorEnabled?: boolean;
  };
  confirmed?: boolean;
};

export function extractObfuscatedAuth(data: unknown): ObfuscatedAuth | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const hasAny = 'c' in d || 'e' in d || 'n' in d || 'l' in d || 'o' in d;
  if (!hasAny) return null;
  return {
    c: typeof d.c === 'boolean' ? (d.c as boolean) : undefined,
    e: typeof d.e === 'string' ? (d.e as string) : undefined,
    n: typeof d.n === 'string' ? (d.n as string) : undefined,
    l: typeof d.l === 'string' ? (d.l as string) : undefined,
    a: typeof d.a === 'string' ? (d.a as string) : undefined,
    o: typeof d.o === 'boolean' ? (d.o as boolean) : undefined,
  };
}

export function storeObfuscatedAuth(data: unknown): void {
  try {
    const obf = extractObfuscatedAuth(data);
    if (!obf) return;
    localStorage.setItem(STORAGE_OBF_AUTH_KEY, JSON.stringify(obf));
  } catch {}
}

/**
 * Reads activityId from the persisted obfuscated-auth payload. `/api/user/me`
 * currently omits `a`, so the current-session detection falls back to the value
 * captured at login. ponytail: obf-in-localStorage fallback; drop once /me returns `a`.
 */
export function readStoredActivityId(): string | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_OBF_AUTH_KEY);
    if (!raw) return undefined;
    const obf = JSON.parse(raw) as ObfuscatedAuth;
    return typeof obf.a === 'string' ? obf.a : undefined;
  } catch {
    return undefined;
  }
}

export function mapAuthResponse(data: unknown): MappedAuthData {
  // Defensive narrowing of unknown to a record
  const d: Record<string, unknown> =
    data && typeof data === 'object' ? (data as Record<string, unknown>) : {};

  const user: Record<string, unknown> =
    d.user && typeof d.user === 'object' ? (d.user as Record<string, unknown>) : {};

  const email =
    (typeof d.e === 'string' ? d.e : undefined) ??
    (typeof d.email === 'string' ? d.email : undefined) ??
    (typeof user.email === 'string' ? (user.email as string) : undefined);

  const name =
    (typeof d.n === 'string' ? d.n : undefined) ??
    (typeof d.nickname === 'string' ? d.nickname : undefined) ??
    (typeof d.name === 'string' ? d.name : undefined) ??
    (typeof user.name === 'string' ? (user.name as string) : undefined);

  const token =
    (typeof d.t === 'string' ? d.t : undefined) ??
    (typeof d.token === 'string' ? d.token : undefined) ??
    (typeof d.accessToken === 'string' ? d.accessToken : undefined);

  const confirmed =
    (typeof d.c === 'boolean' ? d.c : undefined) ??
    (typeof d.confirmed === 'boolean' ? d.confirmed : undefined);

  const language =
    (typeof d.l === 'string' ? d.l : undefined) ??
    (typeof d.language === 'string' ? d.language : undefined);

  const userGroup =
    (typeof d.userGroup === 'string' ? d.userGroup : undefined) ??
    (typeof user.userGroup === 'string' ? (user.userGroup as string) : undefined);

  // User ID is no longer derived from the JWT (token is HttpOnly; identity comes from /api/user/me in Task 5)
  const id: string | undefined = undefined;

  const activityId =
    (typeof d.a === 'string' ? d.a : undefined) ??
    (typeof user.activityId === 'string' ? (user.activityId as string) : undefined);

  const twoFactorEnabled =
    (typeof d.o === 'boolean' ? d.o : undefined) ??
    (typeof user.twoFactorEnabled === 'boolean' ? (user.twoFactorEnabled as boolean) : undefined);

  const result: MappedAuthData = {
    token,
    user: {
      id,
      email,
      name,
      confirmed,
      userGroup,
      language,
      activityId,
      twoFactorEnabled,
    },
    confirmed,
  };

  return result;
}
